import React, { useState, useEffect, useCallback } from 'react';
import { Button, Spin, notification, Dropdown, Menu } from 'antd';
import { Table } from '_src/components';
import { Select, Input } from '_src/shared/form-helpers';
import { useFetch } from 'store';
import services from '_src/services';
import moment from 'moment';

import { FiltersRow, ButtonsRow, StatusBadge } from '../styles';
import AddEditLaptopModal from './AddEditLaptopModal';
import RetireLaptopModal from './RetireLaptopModal';
import TransferLaptopModal from './TransferLaptopModal';
import MaintenanceLogModal from './MaintenanceLogModal';

const laptopConfig = services['laptopLending'];

export default function LaptopInventory({ branchId, branches = [] }) {
  const [laptops, setLaptops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLaptop, setSelectedLaptop] = useState(null);
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });
  const [settings, setSettings] = useState({
    allowTransfer: false,
    trackMaintenance: false
  });

  const { get: fetchLaptops } = useFetch(laptopConfig.getLaptops);
  const { get: fetchSettings } = useFetch(laptopConfig.getSettings);

  const loadSettings = useCallback(async () => {
    try {
      const response = await fetchSettings({ params: { branch_id: branchId } });
      const settingsList = response?.settings || response?.[0]?.data?.settings || [];

      const findSetting = (key) => settingsList.find(s => s.setting_key === key);
      setSettings({
        allowTransfer: findSetting('allow_branch_transfer')?.enabled || false,
        trackMaintenance: findSetting('track_maintenance')?.enabled || false
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  }, [branchId]);

  const loadLaptops = useCallback(async () => {
    if (!branchId) return;

    setLoading(true);
    try {
      const params = { branch_id: branchId };
      if (filters.status !== 'all') {
        params.status = filters.status;
      }
      if (filters.search) {
        params.search = filters.search;
      }

      const response = await fetchLaptops({ params });
      const laptopList = response?.laptops || response?.[0]?.data?.laptops || [];
      setLaptops(laptopList.map((l, i) => ({ ...l, key: l.id || i })));
    } catch (error) {
      notification.error({ message: 'Failed to load laptops', duration: 5 });
    }
    setLoading(false);
  }, [branchId, filters]);

  useEffect(() => {
    loadLaptops();
    loadSettings();
  }, [loadLaptops, loadSettings]);

  const openEditModal = (laptop) => {
    setSelectedLaptop(laptop);
    setShowAddModal(true);
  };

  const openRetireModal = (laptop) => {
    setSelectedLaptop(laptop);
    setShowRetireModal(true);
  };

  const openTransferModal = (laptop) => {
    setSelectedLaptop(laptop);
    setShowTransferModal(true);
  };

  const openMaintenanceModal = (laptop) => {
    setSelectedLaptop(laptop);
    setShowMaintenanceModal(true);
  };

  const closeAllModals = () => {
    setSelectedLaptop(null);
    setShowAddModal(false);
    setShowRetireModal(false);
    setShowTransferModal(false);
    setShowMaintenanceModal(false);
  };

  const getStatusLabel = (status) => {
    const labels = {
      available: 'Available',
      checked_out: 'Checked Out',
      on_loan: 'On Loan',
      maintenance: 'Maintenance',
      retired: 'Retired',
      damaged: 'Damaged'
    };
    return labels[status] || status;
  };

  const getActionsMenu = (record) => {
    return (
      <Menu>
        <Menu.Item key="edit" onClick={() => openEditModal(record)}>
          Edit
        </Menu.Item>
        {settings.trackMaintenance && record.status !== 'retired' && (
          <Menu.Item key="maintenance" onClick={() => openMaintenanceModal(record)}>
            Add Maintenance Log
          </Menu.Item>
        )}
        {settings.allowTransfer && record.status !== 'retired' && record.status !== 'checked_out' && record.status !== 'on_loan' && (
          <Menu.Item key="transfer" onClick={() => openTransferModal(record)}>
            Transfer to Branch
          </Menu.Item>
        )}
        {record.status !== 'retired' && record.status !== 'checked_out' && record.status !== 'on_loan' && (
          <Menu.Item key="retire" onClick={() => openRetireModal(record)} danger>
            Retire
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const columns = [
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand'
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model'
    },
    {
      title: 'Serial Number',
      key: 'serial_number',
      render: (_, record) => record.serialNumber || record.serial_number || '-'
    },
    {
      title: 'Asset Tag',
      key: 'asset_tag',
      render: (_, record) => record.assetTag || record.asset_tag || '-'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <StatusBadge status={status}>{getStatusLabel(status)}</StatusBadge>
      )
    },
    {
      title: 'Current Student',
      key: 'current_student',
      render: (_, record) => {
        const loan = record.currentLoan || record.current_loan;
        if (loan && loan.student) {
          const s = loan.student;
          return `${s.firstName || s.first_name || ''} ${s.lastName || s.last_name || ''}`.trim();
        }
        return '-';
      }
    },
    {
      title: 'Added',
      key: 'created_at',
      render: (_, record) => {
        const val = record.createdAt || record.created_at;
        return val ? moment(val).format('MM/DD/YYYY') : '-';
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Dropdown overlay={getActionsMenu(record)} trigger={['click']}>
          <Button size="small">
            Actions
          </Button>
        </Dropdown>
      )
    }
  ];

  return (
    <div>
      <FiltersRow>
        <Input
          label="Search"
          placeholder="Serial number, brand, model..."
          value={filters.search}
          onChange={value => setFilters(prev => ({ ...prev, search: value }))}
          style={{ width: 250 }}
          allowClear
        />

        <Select
          label="Status"
          value={filters.status}
          onChange={value => setFilters(prev => ({ ...prev, status: value }))}
          options={[
            { id: 'all', label: 'All Statuses' },
            { id: 'available', label: 'Available' },
            { id: 'checked_out', label: 'Checked Out' },
            { id: 'maintenance', label: 'Maintenance' },
            { id: 'retired', label: 'Retired' }
          ]}
          style={{ width: 180 }}
        />

        <Button onClick={loadLaptops} style={{ marginLeft: 'auto' }}>
          Refresh
        </Button>
      </FiltersRow>

      <ButtonsRow>
        <Button type="primary" onClick={() => setShowAddModal(true)}>
          Add Laptop
        </Button>
      </ButtonsRow>

      <Spin spinning={loading}>
        <Table
          dataSource={laptops}
          columns={columns}
          pagination={{ pageSize: 20 }}
        />
      </Spin>

      <AddEditLaptopModal
        visible={showAddModal && !selectedLaptop}
        onClose={closeAllModals}
        laptop={null}
        branchId={branchId}
        onSaved={loadLaptops}
      />

      <AddEditLaptopModal
        visible={showAddModal && !!selectedLaptop}
        onClose={closeAllModals}
        laptop={selectedLaptop}
        branchId={branchId}
        onSaved={loadLaptops}
      />

      <RetireLaptopModal
        visible={showRetireModal}
        onClose={closeAllModals}
        laptop={selectedLaptop}
        onRetired={loadLaptops}
      />

      <TransferLaptopModal
        visible={showTransferModal}
        onClose={closeAllModals}
        laptop={selectedLaptop}
        currentBranchId={branchId}
        branches={branches}
        onTransferred={loadLaptops}
      />

      <MaintenanceLogModal
        visible={showMaintenanceModal}
        onClose={closeAllModals}
        laptop={selectedLaptop}
        onSaved={loadLaptops}
      />
    </div>
  );
}
